import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users, ArrowRight } from "lucide-react";
import { fetchAPI } from "@/lib/api";
import { throttle } from "@/lib/utils";

interface Circuit {
  id: number;
  title: string;
  location: string;
  duration: string;
  group_size: string;
  travel_dates: string;
  price: string;
  image_url: string;
  tag: string;
}

export const FeaturedJourneys = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLAnchorElement | null)[]>([]); // Ref array for cards
  const [journeys, setJourneys] = useState<Circuit[]>([]);

  useEffect(() => {
    fetchAPI("?type=circuits")
      .then((data: any) => {
        // Take first 3 for featured
        setJourneys(data.slice(0, 3));
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                el.classList.add("visible");
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [journeys]); // Add journeys dependency to re-observe if needed

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const baseOffset = (scrollProgress - 0.5) * 30;

      // Direct DOM manipulation
      cardsRef.current.forEach((card, index) => {
        if (!card) return;
        let factor = 1.0;
        if (index === 0) factor = 0.8;
        if (index === 2) factor = 1.2;
        
        const offset = baseOffset * factor;
        card.style.transform = `translateY(${offset}px)`;
      });

    }, 50);

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []); // Empty dependency array as we use refs

  if (journeys.length === 0) return null;

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="reveal text-saffron text-sm tracking-[0.2em] uppercase font-medium">
              Upcoming Journeys
            </span>
            <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mt-4">
              Begin Your <span className="text-maroon">Journey</span>
            </h2>
          </div>
          <Link to="/circuits" className="reveal">
            <Button variant="outline" className="group">
              View All Journeys
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* Journeys Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {journeys.map((journey, index) => (
            <Link
              key={journey.id}
              to="/circuits"
              className="reveal group block" // Added block
              ref={(el) => (cardsRef.current[index] = el)} // Attach ref
              style={{ 
                transitionDelay: `${index * 100}ms`,
                // Initial transform handled by CSS/Layout, parallax added via JS
                transition: "transform 0.1s linear" // Smoother direct update
              }}
            >
              <article className="bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-500 hover:-translate-y-2 h-full flex flex-col">
                {/* Image */}
                <div className="relative h-64 overflow-hidden shrink-0">
                  <img
                    src={`https://baelleaves.com/${journey.image_url}`}
                    alt={journey.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo/60 to-transparent" />
                  
                  {/* Tag */}
                  {journey.tag && (
                    <span className="absolute top-4 left-4 bg-gold/90 text-foreground text-xs font-medium px-3 py-1 rounded-full">
                      {journey.tag}
                    </span>
                  )}
                  
                  {/* Price */}
                  <div className="absolute bottom-4 right-4 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-lg">
                    <span className="text-saffron font-medium">{journey.price}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-serif text-xl text-foreground mb-3 group-hover:text-saffron transition-colors">
                    {journey.title}
                  </h3>

                  {/* Details */}
                  <div className="space-y-2 text-sm text-muted-foreground mt-auto">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-saffron" />
                      <span>{journey.location}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-saffron" />
                        <span>{journey.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-saffron" />
                        <span>{journey.group_size}</span>
                      </div>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm">
                      <span className="text-muted-foreground">Next departure: </span>
                      <span className="text-foreground font-medium">{journey.travel_dates}</span>
                    </p>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
